// The create-team drawer (teams settings, UI-LAW §4a Drawer): deep-linked by
// the CALLER via `?new=1` — this component renders only while the param is set
// and the caller can manage, so field state resets on close by unmount. The
// Display name field is the panel's first focusable element, which the Drawer
// focus trap targets on open.

import type { SyntheticEvent } from "react";
import { useState } from "react";

import {
  Button,
  Drawer,
  Inline,
  InlineError,
  Stack,
  TextInput,
} from "../../ui";

export interface TeamCreateDrawerProps {
  // Blocking create failure — stays inline in the drawer (UI-LAW §4a: a toast
  // is never the sole surface for a blocking error).
  createErrorMessage?: string;
  isCreatePending: boolean;
  onDismiss: () => void;
  onSubmit: (displayName: string) => void;
}

export function TeamCreateDrawer({
  createErrorMessage,
  isCreatePending,
  onDismiss,
  onSubmit,
}: TeamCreateDrawerProps) {
  const [displayName, setDisplayName] = useState("");

  const submitDisabled = isCreatePending || displayName.trim().length === 0;

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmit(displayName.trim());
  }

  return (
    <Drawer ariaLabel="Create team" isOpen onDismiss={onDismiss}>
      <form onSubmit={handleSubmit}>
        <Stack gap={3}>
          <TextInput
            label="Display name"
            required
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.currentTarget.value);
            }}
          />
          <Inline gap={3}>
            <Button disabled={submitDisabled} type="submit">
              {isCreatePending ? "Creating..." : "Create"}
            </Button>
            <Button
              disabled={isCreatePending}
              type="button"
              variant="secondary"
              onClick={onDismiss}
            >
              Cancel
            </Button>
          </Inline>
          {createErrorMessage ? (
            <InlineError>{createErrorMessage}</InlineError>
          ) : null}
        </Stack>
      </form>
    </Drawer>
  );
}
